import { useEffect, useRef, useState } from 'react';
import { Icon } from '@/components/ui/Icon';
import { FormSelect } from '@/components/ui/FormSelect';
import { Toggle } from '@/components/ui/Toggle';
import { micConstraint, useAudioDevices } from '../stores/audioDevices';
import { gateStream, type GatedStream } from '../audio/noiseGate';
import { toast } from '@/components/ui/Toast';

interface Dev { deviceId: string; label: string }

type SinkableAudio = HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };

/**
 * Microphone + speaker selection for the softphone. Enumerates the browser's
 * audio devices (requires a one-time mic permission to reveal labels), persists
 * the choice, and offers LIVE tests: the mic test runs a level meter AND plays
 * the mic back through the CHOSEN headphones (self-monitor — "do I sound
 * good"), and the speaker test plays a tone through the chosen output
 * device (setSinkId).
 */
export function AudioDeviceSettings() {
  const {
    micId, speakerId, setMic, setSpeaker,
    noiseSuppression, setNoiseSuppression,
    echoCancellation, setEchoCancellation,
    autoGainControl, setAutoGainControl,
    speakerVolume, setSpeakerVolume,
    micGain, setMicGain,
    gateEnabled, setGateEnabled,
    gateThreshold, setGateThreshold,
  } = useAudioDevices();
  const [mics, setMics] = useState<Dev[]>([]);
  const [speakers, setSpeakers] = useState<Dev[]>([]);
  const [granted, setGranted] = useState(false);
  const [level, setLevel] = useState(0);
  const [testing, setTesting] = useState(false);
  const micCleanup = useRef<(() => void) | null>(null);
  const supportsSink = typeof (HTMLMediaElement.prototype as unknown as { setSinkId?: unknown }).setSinkId === 'function';

  async function enumerate() {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    const list = await navigator.mediaDevices.enumerateDevices();
    setMics(list.filter((d) => d.kind === 'audioinput').map((d, i) => ({ deviceId: d.deviceId, label: d.label || `מיקרופון ${i + 1}` })));
    setSpeakers(list.filter((d) => d.kind === 'audiooutput').map((d, i) => ({ deviceId: d.deviceId, label: d.label || `רמקול ${i + 1}` })));
    setGranted(list.some((d) => d.kind === 'audioinput' && d.label !== ''));
  }

  useEffect(() => {
    void enumerate();
    navigator.mediaDevices?.addEventListener?.('devicechange', enumerate);
    return () => {
      navigator.mediaDevices?.removeEventListener?.('devicechange', enumerate);
      micCleanup.current?.(); // never leave a live self-monitor running after unmount
    };
  }, []);

  async function requestPermission() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      await enumerate();
      toast('הרשאת מיקרופון התקבלה', 'ok');
    } catch {
      toast('ההרשאה למיקרופון נדחתה', 'danger');
    }
  }

  /** Route a MediaStream to the CHOSEN speaker via a sinkable <audio> element. */
  async function playOnChosenSpeaker(stream: MediaStream): Promise<SinkableAudio> {
    const el = new Audio() as SinkableAudio;
    el.srcObject = stream;
    el.volume = Math.max(0, Math.min(1, speakerVolume / 100));
    if (speakerId && typeof el.setSinkId === 'function') {
      try {
        await el.setSinkId(speakerId);
      } catch {
        // Device unplugged / permission changed — fall back to default output
        // but tell the user their chosen device did not receive the sound.
        toast('ההתקן שנבחר אינו זמין — הצליל הושמע דרך ברירת המחדל', 'warn');
      }
    }
    await el.play();
    return el;
  }

  // Live mic test (~8s): the agent HEARS THEMSELVES through the chosen
  // headphones while the level meter runs — verifying in one go that the mic
  // picks up AND how it actually sounds. Uses micConstraint(), i.e. the SAME
  // quality tuning a real call uses — so tweaking the knobs below is heard
  // immediately. ONE exception: echo cancellation is forced OFF for the test —
  // AEC hears the self-monitor playback as "echo" and gates the mic, which
  // makes the loop sound choppy/broken. Real calls keep the user's AEC setting
  // (there is no self-loop in a call, so it doesn't chop there).
  async function testMic() {
    if (testing) { micCleanup.current?.(); return; }
    setTesting(true);
    try {
      const constraints = micConstraint(noiseSuppression);
      constraints.echoCancellation = { ideal: false };
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: constraints,
      });
      // Noise gate first (same order as a real call: gate → boost), so the
      // agent can TUNE the threshold by ear: talk = passes, neighbor = blocked.
      let gated: GatedStream | null = null;
      let monitored: MediaStream = stream;
      if (gateEnabled) {
        try {
          gated = gateStream(stream, gateThreshold);
          monitored = gated.stream;
        } catch { /* gate failed — test the raw mic */ }
      }
      const ctx = new AudioContext();
      const src = ctx.createMediaStreamSource(monitored);
      // Mic gain — the SAME boost a real call applies (fix for a quiet mic).
      // Both the meter and the self-monitor run through it, so dragging the
      // "עוצמת מיקרופון" slider is heard/seen immediately on the next test.
      const gainNode = ctx.createGain();
      gainNode.gain.value = Math.max(0, micGain / 100);
      src.connect(gainNode);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      gainNode.connect(analyser);
      // self-monitor: mic → gain → chosen speaker, so "שומעים טוב" is verified live.
      const monitorDest = ctx.createMediaStreamDestination();
      gainNode.connect(monitorDest);
      const el = await playOnChosenSpeaker(monitorDest.stream);
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const end = Date.now() + 8000;
      let done = false;
      const stop = () => {
        if (done) return;
        done = true;
        gated?.dispose();
        stream.getTracks().forEach((t) => t.stop());
        el.pause();
        el.srcObject = null;
        void ctx.close();
        micCleanup.current = null;
        setLevel(0);
        setTesting(false);
      };
      micCleanup.current = stop;
      const tick = () => {
        if (done) return;
        analyser.getByteTimeDomainData(buf);
        let peak = 0;
        for (const v of buf) peak = Math.max(peak, Math.abs(v - 128));
        setLevel(Math.min(100, Math.round((peak / 128) * 100)));
        if (Date.now() < end) requestAnimationFrame(tick);
        else stop();
      };
      tick();
    } catch {
      toast('לא ניתן לגשת למיקרופון', 'danger');
      setTesting(false);
    }
  }

  // Short test tone — played through the CHOSEN speaker (setSinkId), so the
  // test actually validates the selected headphones, not the system default.
  async function testSpeaker() {
    try {
      const ctx = new AudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const dest = ctx.createMediaStreamDestination();
      osc.frequency.value = 440;
      gain.gain.value = 0.15;
      osc.connect(gain).connect(dest);
      const el = await playOnChosenSpeaker(dest.stream);
      osc.start();
      setTimeout(() => {
        osc.stop();
        el.pause();
        el.srcObject = null;
        void ctx.close();
      }, 600);
    } catch {
      toast('השמעת צליל הבדיקה נכשלה', 'danger');
    }
  }

  return (
    <div className="stack" style={{ gap: 14 }}>
      {!granted && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, background: 'var(--surface-2)', borderRadius: 'var(--r-2)', padding: 'var(--pad-row) var(--pad-card)' }}>
          <span style={{ fontSize: 'var(--t-sm)', color: 'var(--text-2)' }}>אפשר גישה למיקרופון כדי לבחור התקנים ולבדוק אותם.</span>
          <button type="button" className="btn primary" onClick={requestPermission}>אפשר גישה</button>
        </div>
      )}

      <div>
        <div style={{ fontSize: 'var(--t-sm)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon name="mic" size={14} /> מיקרופון
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <FormSelect aria-label="בחירת מיקרופון" value={micId ?? ''} onChange={(e) => setMic(e.target.value || null)}>
            <option value="">ברירת מחדל של המערכת</option>
            {mics.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label}</option>)}
          </FormSelect>
          <button type="button" className={testing ? 'btn danger' : 'btn'} onClick={testMic}>{testing ? 'עצור' : 'בדיקה'}</button>
        </div>
        {/* mic level meter */}
        <div style={{ height: 8, background: 'var(--surface-2)', borderRadius: 'var(--r-pill)', overflow: 'hidden', marginTop: 6 }}>
          <div style={{ width: `${level}%`, height: '100%', background: level > 70 ? 'var(--warn)' : 'var(--ok)', transition: 'width 60ms linear' }} />
        </div>
        {/* mic input gain — the fix for a mic that's simply too quiet. */}
        <div style={{ marginTop: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <span style={{ fontSize: 'var(--t-sm)' }}>עוצמת מיקרופון</span>
            <span style={{ fontSize: 'var(--t-xs)', color: micGain !== 100 ? 'var(--accent)' : 'var(--muted)', fontVariantNumeric: 'tabular-nums', fontWeight: micGain !== 100 ? 600 : 400 }}>
              {micGain}%
            </span>
          </div>
          <input
            type="range"
            min={25}
            max={400}
            step={5}
            value={micGain}
            onChange={(e) => setMicGain(Number(e.target.value))}
            aria-label="עוצמת מיקרופון"
            style={{ width: '100%', accentColor: 'var(--accent)', marginTop: 4 }}
          />
          <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 2 }}>
            מיקרופון חלש? הגבר מעל 100%. ההגברה חלה על הבדיקה וגם על שיחות אמיתיות — הרץ בדיקה אחרי שינוי כדי לכוון עד שהמד מגיע לירוק גבוה בלי להיצבע כתום.
          </div>
        </div>
        <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 8 }}>
          בזמן הבדיקה דבר למיקרופון — תשמע את עצמך באוזניות שנבחרו ותראה את עוצמת הקול במד.
        </div>
      </div>

      <div>
        <div style={{ fontSize: 'var(--t-sm)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon name="headphones" size={14} /> אוזניות / רמקול
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <FormSelect
            aria-label="בחירת אוזניות או רמקול"
            value={speakerId ?? ''}
            onChange={(e) => setSpeaker(e.target.value || null)}
            disabled={!supportsSink}
          >
            <option value="">ברירת מחדל של המערכת</option>
            {speakers.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label}</option>)}
          </FormSelect>
          <button type="button" className="btn" onClick={testSpeaker}>נגן צליל</button>
        </div>
        {!supportsSink && (
          <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 4 }}>
            בחירת רמקול אינה נתמכת בדפדפן זה — תושמע דרך התקן ברירת המחדל.
          </div>
        )}
      </div>

      {/* Background-noise suppression toggle (RNNoise on the outbound mic). */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div style={{ fontSize: 'var(--t-sm)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon name="mic" size={14} /> סינון רעשי רקע
          </div>
          <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 2, maxWidth: 360 }}>
            מסנן רעשים והדברה ברקע (אנשים שמדברים לידך, מזגן) כך שלא יישמעו בצד השני. מומלץ להשאיר דלוק.
          </div>
        </div>
        <span aria-label="סינון רעשי רקע">
          <Toggle checked={noiseSuppression} onChange={setNoiseSuppression} />
        </span>
      </div>

      {/* Noise gate — blocks NEARBY VOICES (which denoisers deliberately keep,
          since they preserve human speech). The agent is close & loud → opens
          the gate; neighbors are quieter → cut. */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div style={{ fontSize: 'var(--t-sm)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon name="mic" size={14} /> חסימת קולות סביבה (שער רעש)
          </div>
          <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 2, maxWidth: 360 }}>
            הלקוח שומע אנשים שמדברים לידך? השער מעביר את המיקרופון רק כשאתה מדבר (קרוב וחזק) וחוסם קולות רחוקים.
          </div>
        </div>
        <span aria-label="חסימת קולות סביבה">
          <Toggle checked={gateEnabled} onChange={setGateEnabled} />
        </span>
      </div>
      {gateEnabled && (
        <div style={{ background: 'var(--surface-2)', borderRadius: 'var(--r-2)', padding: 'var(--pad-row) var(--pad-card)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <span style={{ fontSize: 'var(--t-sm)' }}>סף החסימה</span>
            <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', fontVariantNumeric: 'tabular-nums' }}>{gateThreshold}</span>
          </div>
          <input
            type="range"
            min={0}
            max={100}
            step={5}
            value={gateThreshold}
            onChange={(e) => setGateThreshold(Number(e.target.value))}
            aria-label="סף החסימה"
            style={{ width: '100%', accentColor: 'var(--accent)', marginTop: 4 }}
          />
          <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 2 }}>
            כוונון: הרץ "בדיקה", בקש ממישהו לידך לדבר, והעלה את הסף עד שהוא נחסם ואתה עדיין עובר חזק וברור. סף גבוה מדי יחתוך גם אותך — אם הקול שלך "נעלם", הורד.
          </div>
        </div>
      )}

      {/* Quality tuning — the same knobs a real call uses (micConstraint), so
          running the mic test after a tweak lets you HEAR the difference. */}
      <div style={{ borderTop: '1px solid var(--line)', paddingTop: 12 }}>
        <div style={{ fontSize: 'var(--t-sm)', fontWeight: 600, marginBottom: 2 }}>כוונון איכות</div>
        <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginBottom: 10 }}>
          שנה פרמטר והרץ שוב את בדיקת המיקרופון — תשמע מיד את ההבדל. ההגדרות חלות גם על שיחות אמיתיות.
        </div>

        <div className="stack" style={{ gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ fontSize: 'var(--t-sm)' }}>ביטול הד</div>
              <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 2, maxWidth: 360 }}>
                מונע מהצד השני לשמוע את עצמו. עם אוזניות איכותיות אפשר לכבות — הקול יישמע טבעי ופחות "מעובד".
              </div>
            </div>
            <span aria-label="ביטול הד">
              <Toggle checked={echoCancellation} onChange={setEchoCancellation} />
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ fontSize: 'var(--t-sm)' }}>הגברה אוטומטית</div>
              <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 2, maxWidth: 360 }}>
                מיישר את עוצמת הקול אוטומטית. אם העוצמה "נושמת" — עולה ויורדת לבד — כבה וקבע עוצמה קבועה במיקרופון.
              </div>
            </div>
            <span aria-label="הגברה אוטומטית">
              <Toggle checked={autoGainControl} onChange={setAutoGainControl} />
            </span>
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ fontSize: 'var(--t-sm)' }}>עוצמת שמע בשיחה</div>
              <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', fontVariantNumeric: 'tabular-nums' }}>{speakerVolume}%</span>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={speakerVolume}
              onChange={(e) => setSpeakerVolume(Number(e.target.value))}
              aria-label="עוצמת שמע בשיחה"
              style={{ width: '100%', accentColor: 'var(--accent)', marginTop: 6 }}
            />
            <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 2 }}>
              עוצמת ההשמעה באוזניות — חלה על הבדיקות כאן ועל שיחות הסופטפון.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
